import { ProductDto } from "@custom-types/products"
import { getDefaultValues } from "@features/administration/products/EditProduct/NodeTagsForm/getDefaultValues"
import { getFormSchema } from "@features/administration/products/EditProduct/NodeTagsForm/getFormSchema"
import { yupResolver } from "@hookform/resolvers/yup"
import * as yup from "yup"
import { useGetNodeTagsQuery } from "@store/services/productsService"
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from "react"
import {
  FormProvider,
  SubmitErrorHandler,
  SubmitHandler,
  useFieldArray,
  useForm,
} from "react-hook-form"
import {
  CircularProgress,
  Grid,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material"
import VirtualizedTagRows from "@features/builder/BulkNodesEditDialog/BulkNodesEditForm/VirtualizedTagRows"
import { downloadFile, readFile } from "@utils/file"
import {
  TAG_FILE_REQUIRED_HEADERS,
  formValuesToNodeTagsFileContent,
  getRowsFromTagsFileContent,
} from "@features/administration/products/EditProduct/nodeTagsHelper"
import { enqueueSnackbar } from "notistack"

export const NODE_TAGS_FORM_ID = "node-tags-form"

type NodeTagsFormProps = {
  product: ProductDto
  onSubmit: SubmitHandler<NodeTagsFormValues>
  onSubmitError: SubmitErrorHandler<NodeTagsFormValues>
}

export type NodeTagsFormValues = yup.InferType<ReturnType<typeof getFormSchema>>
export type NodeTagRowFormValues = NodeTagsFormValues["rows"][number]

export type NodeTagsFormRef = {
  addTag: (newTag: NodeTagRowFormValues) => void
  exportTagsFile: () => void
  importTagsFile: (file: File) => void
}

const NodeTagsForm = forwardRef<NodeTagsFormRef, NodeTagsFormProps>((props, ref) => {
  const tableContainerRef = useRef<HTMLDivElement>(null)
  const { data: tagsResponse, isLoading } = useGetNodeTagsQuery(
    {
      key: props.product.key,
      orgKey: props.product.organisation_key,
      divKey: props.product.division_key,
    },
    {
      skip: props.product.tags_s3_location === "",
    },
  )

  const formMethods = useForm<NodeTagsFormValues>({
    defaultValues: getDefaultValues(tagsResponse),
    resolver: yupResolver(getFormSchema()),
  })

  useEffect(() => {
    if (!tagsResponse) return

    formMethods.reset(getDefaultValues(tagsResponse))
  }, [formMethods, props.product, tagsResponse])

  const { fields, append, remove } = useFieldArray({ control: formMethods.control, name: "rows" })

  const handleAddTag = useCallback(
    (newTagRow: NodeTagsFormValues["rows"][number]) => {
      if (!tableContainerRef.current) return

      append(newTagRow)

      // scroll to the bottom of the table to show the newly added row
      tableContainerRef.current.scrollTo({
        top: tableContainerRef.current.scrollHeight,
        behavior: "smooth",
      })
    },
    [append],
  )

  const handleExportTags = useCallback(() => {
    const fileContent = formValuesToNodeTagsFileContent(formMethods.getValues())

    const blob = new Blob([fileContent], { type: "text/csv;charset=utf-8" })
    const blobUrl = URL.createObjectURL(blob)

    downloadFile(`node-tags-${props.product.key}.csv`, blobUrl)
    URL.revokeObjectURL(blobUrl)
  }, [formMethods, props.product.key])

  const handleImportTagsFile = useCallback(
    async (file: File) => {
      const fileAsString = await readFile(file)
      const newRows = getRowsFromTagsFileContent(fileAsString)

      if (newRows.length === 0) {
        enqueueSnackbar(
          `Invalid CSV file. Please check if given file has correct headers and values.
        Required headers are: ${TAG_FILE_REQUIRED_HEADERS.join(", ")}`,
          { variant: "error" },
        )
        return
      }

      formMethods.reset(getDefaultValues(fileAsString), { keepDefaultValues: false })
    },
    [formMethods],
  )

  // allow parent component to call callbacks that should have direct impact on the form state
  useImperativeHandle(
    ref,
    () => ({
      addTag: handleAddTag,
      exportTagsFile: handleExportTags,
      importTagsFile: handleImportTagsFile,
    }),
    [handleAddTag, handleExportTags, handleImportTagsFile],
  )

  if (isLoading) {
    return (
      <Grid container direction="column">
        <Grid item alignSelf="center">
          <CircularProgress />
        </Grid>
      </Grid>
    )
  }

  return (
    <FormProvider {...formMethods}>
      <form
        autoComplete="off"
        onSubmit={formMethods.handleSubmit(props.onSubmit, props.onSubmitError)}
        id={NODE_TAGS_FORM_ID}
      >
        <TableContainer
          ref={tableContainerRef}
          sx={{
            overflowX: "initial",
            overflowY: "auto",
            maxHeight: "calc(100dvh - 300px)",
          }}
        >
          <Table stickyHeader size="small" sx={{ tableLayout: "fixed" }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 20 }}>
                  <strong>No.</strong>
                </TableCell>
                <TableCell sx={{ width: 130 }}>
                  <strong>Name</strong>
                </TableCell>
                <TableCell sx={{ width: 130 }}>
                  <strong>Type</strong>
                </TableCell>
                <TableCell sx={{ width: 150 }}>
                  <strong>Option value</strong>
                </TableCell>
                <TableCell sx={{ width: 200 }}>
                  <strong>Option description</strong>
                </TableCell>
                <TableCell sx={{ width: 40 }}>
                  <strong>Actions</strong>
                </TableCell>
              </TableRow>
            </TableHead>

            <VirtualizedTagRows
              tableContainerRef={tableContainerRef}
              fields={fields}
              onDelete={remove}
            />
          </Table>
        </TableContainer>
      </form>
    </FormProvider>
  )
})

NodeTagsForm.displayName = "NodeTagsForm"

export default NodeTagsForm
